import React, { useState, useEffect } from 'react';
import axios from "axios";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { BASE_URL } from '../../App';
import 'bootstrap/dist/css/bootstrap.min.css';

const getDateRange = (type) => {
    const today = new Date();
    let startDate, endDate;

    if (type === 'today') {
        startDate = endDate = today.toISOString().slice(0, 10);
    } else if (type === 'week') {
        const first = today.getDate() - today.getDay();
        const last = first + 6;
        const start = new Date(today.setDate(first));
        const end = new Date(today.setDate(last));
        startDate = start.toISOString().slice(0, 10);
        endDate = end.toISOString().slice(0, 10);
    } else if (type === 'month') {
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        startDate = start.toISOString().slice(0, 10);
        endDate = end.toISOString().slice(0, 10);
    }
    return { startDate, endDate };
};



const RetailerSalesOrderReport = () => {
    const [salesData, setSalesData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [rangeType, setRangeType] = useState('today');
    const [dateRange, setDateRange] = useState(getDateRange('today'));

    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');

    useEffect(() => {
        setDateRange(getDateRange(rangeType));
    }, [rangeType]);

    useEffect(() => {
        fetchSalesData();
    }, [dateRange]);

    const fetchSalesData = async () => {
        try {
            setLoading(true);
            const data = {
                startDate: dateRange.startDate,
                endDate: dateRange.endDate
            };
            const response = await axios.post(`${BASE_URL}/retailer_sales_order_report`, data);
            setSalesData(response.data.data);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        const startDate = customStartDate || dateRange.startDate;
        const endDate = customEndDate || dateRange.endDate;
        setDateRange({ startDate, endDate });
    };

    if (loading) return <div className="text-center mt-5"><div className="spinner-border" role="status"></div></div>;
    if (error) return <div className="alert alert-danger mt-3">Error: {error.message}</div>;

    const headers = salesData.length > 0 ? Object.keys(salesData[0]) : [];

    // Format header
        const formatHeader = (text) => text.replace(/_/g, ' ').toUpperCase();

        // Excel
        const exportToExcel = () => {
            const wsData = [headers.map(formatHeader)];
            salesData.forEach(row => {
                wsData.push(headers.map(header => row[header]));
            });

            const ws = XLSX.utils.aoa_to_sheet(wsData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Report');
            XLSX.writeFile(wb, 'Retailer_Sales_Report.xlsx');
        };

        // CSV
        const exportToCSV = () => {
            const ws = XLSX.utils.json_to_sheet(salesData, { header: headers });

            // Step 2: Replace header row with formatted (uppercase, space-separated) headers
            headers.forEach((key, idx) => {
                const cellAddress = XLSX.utils.encode_cell({ r: 0, c: idx }); // First row (0), column index
                ws[cellAddress].v = key.replace(/_/g, ' ').toUpperCase();     // Update cell value
            });
            const csvOutput = XLSX.utils.sheet_to_csv(ws);
            const blob = new Blob([csvOutput], { type: 'text/csv;charset=utf-8;' });
            saveAs(blob, 'Retailer_Sales_Report.csv');
        };

        // PDF
        const exportToPDF = () => {
            const doc = new jsPDF();
            const tableColumn = headers.map(h => h.replace(/_/g, ' ').toUpperCase());
            const tableRows = salesData.map(row => headers.map(header => row[header]));

            // Make sure autoTable is called as a function
            autoTable(doc, {
                head: [tableColumn],
                body: tableRows,
                startY: 20,
                styles: { fontSize: 8 },
            });

            doc.save('Retailer_Sales_Report.pdf');
            };

    return (
        <div className="mt-4 p-4">
            <h1 className="mb-4">Retailer Sales Order Report</h1>
            <div className="row mb-3 align-items-end">
                    <div className="col-md-3">
                        <label className="form-label"  style={{ fontWeight: 'bold' }}>Quick Date Range:</label>
                        <select
                            className="form-select"
                            value={rangeType}
                            onChange={(e) => {
                                setRangeType(e.target.value);
                                setCustomStartDate('');
                                setCustomEndDate('');
                            }}
                        >
                            <option value="">Select</option>
                            <option value="today">Today</option>
                            <option value="week">This Week</option>
                            <option value="month">This Month</option>
                        </select>
                    </div>

                    <div className="col-md-3">
                        <label className="form-label"  style={{ fontWeight: 'bold' }}>From:</label>
                        <input
                            type="date"
                            className="form-control"
                            value={customStartDate}
                            onChange={(e) => setCustomStartDate(e.target.value)}
                        />
                    </div>

                    <div className="col-md-3">
                        <label className="form-label" style={{ fontWeight: 'bold' }}>To:</label>
                        <input
                            type="date"
                            className="form-control"
                            value={customEndDate}
                            onChange={(e) => setCustomEndDate(e.target.value)}
                        />
                    </div>

                    <div className="col-md-3">
                        <button className="btn btn-primary w-100" onClick={handleSearch}>
                            Search
                        </button>
                    </div>
                </div>

            <div className="mb-3 text-center">
                <strong>Showing Data From:</strong> {dateRange.startDate} <strong>To:</strong> {dateRange.endDate}
            </div>

            <div className="table-responsive">
                 {salesData.length === 0 ? (
                            <></>
                        ) : (
                <div className="mb-0 mt-0 d-flex justify-content-end">
                    <div className="btn-group" role="group">
                        <button className="btn btn-success btn-sm" onClick={exportToExcel}>Excel</button>
                        <button className="btn btn-warning btn-sm" onClick={exportToCSV}>CSV</button>
                        <button className="btn btn-danger btn-sm" onClick={exportToPDF}>PDF</button>
                    </div>
                </div>
                        )}
                
                <table className="table table-bordered table-striped">
                    <thead className="table-success">
                        <tr>
                            {headers.map((head) => (
                                <th key={head}>{head.replace(/_/g, ' ').toUpperCase()}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {salesData.length === 0 ? (
                            <tr>
                                <td colSpan={headers.length} className="text-center">No data available</td>
                            </tr>
                        ) : (
                            salesData.map((row, idx) => (
                                <tr key={idx}>
                                    {headers.map((head) => (
                                        <td key={head}>{row[head]}</td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
           
        </div>
    );
};

export default RetailerSalesOrderReport;
